import { CONTENT_DIGEST, CONTENT_VERSION } from "./build-identity.js";
import type { RoomHello, RoomOrder, RoomOrderAck, RoomRole } from "./room-types.js";
import type { AdmissionReceipt, ConflictIntent } from "./types.js";

export const ROOM_PROTOCOL_VERSION = 1;
export const ROOM_APP_ID = CONTENT_VERSION;
export const ROOM_SIGNALING = Object.freeze({
  production: "trystero-nostr",
  test: "trystero-ws-relay",
  version: "0.25.4",
});

export function localRoomHello(
  role: RoomRole,
  sessionId: string,
  reconnectToken: string,
): RoomHello {
  return Object.freeze({
    protocolVersion: ROOM_PROTOCOL_VERSION,
    contentDigest: CONTENT_DIGEST,
    role,
    sessionId: requiredRoomToken(sessionId, "session ID"),
    reconnectToken: requiredRoomToken(reconnectToken, "reconnect token"),
  });
}

export function decodeRoomHello(value: unknown): RoomHello {
  const record = plainRecord(value, "room handshake");
  if (record.protocolVersion !== ROOM_PROTOCOL_VERSION)
    throw new TypeError("Incompatible room protocol version");
  if (record.contentDigest !== CONTENT_DIGEST)
    throw new TypeError("Incompatible Kilnfront ruleset/content hash");
  if (!isRole(record.role)) throw new TypeError("Invalid room role");
  return localRoomHello(
    record.role,
    requiredRoomToken(record.sessionId, "session ID"),
    requiredRoomToken(record.reconnectToken, "reconnect token"),
  );
}

export function decodeRoomOrder(value: unknown): RoomOrder {
  const record = plainRecord(value, "room order");
  const allowed = new Set([
    "roomId",
    "authorityEpoch",
    "controllerGeneration",
    "requestId",
    "controllerSequence",
    "expectedScopeGeneration",
    "intent",
  ]);
  if (Object.keys(record).some((key) => !allowed.has(key)))
    throw new TypeError("Room order may not claim a seat or authority field");
  const controllerSequence = record.controllerSequence;
  if (!Number.isSafeInteger(controllerSequence) || (controllerSequence as number) < 1)
    throw new TypeError("Invalid controller sequence");
  if (!Number.isSafeInteger(record.authorityEpoch) || (record.authorityEpoch as number) < 1)
    throw new TypeError("Invalid room authority epoch");
  if (
    !Number.isSafeInteger(record.controllerGeneration) ||
    (record.controllerGeneration as number) < 0
  )
    throw new TypeError("Invalid room controller generation");
  const intent = plainRecord(record.intent, "room intent") as unknown as ConflictIntent;
  return Object.freeze({
    roomId: requiredRoomToken(record.roomId, "room ID"),
    authorityEpoch: record.authorityEpoch as number,
    controllerGeneration: record.controllerGeneration as number,
    requestId: requiredRoomToken(record.requestId, "request ID"),
    controllerSequence: controllerSequence as number,
    expectedScopeGeneration: requiredRoomToken(record.expectedScopeGeneration, "scope generation"),
    intent: structuredClone(intent),
  });
}

export function requiredRoomToken(value: unknown, label: string): string {
  if (typeof value !== "string" || value.length < 1 || value.length > 128)
    throw new TypeError(`Invalid ${label}`);
  return value;
}

export function acceptedRoomAck(receipt: AdmissionReceipt): RoomOrderAck {
  return Object.freeze({
    ok: true,
    requestId: receipt.requestId,
    seatId: receipt.seatId,
    assignedBoundaryMs: receipt.assignedBoundaryMs,
    coalesced: receipt.coalesced,
  });
}

function plainRecord(value: unknown, label: string): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new TypeError(`Invalid ${label}`);
  return value as Record<string, unknown>;
}

function isRole(value: unknown): value is RoomRole {
  return value === "host" || value === "controller" || value === "spectator";
}
